import React from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, AwardIcon, BookOpenIcon, GraduationCapIcon, BuildingIcon, PhoneIcon, MailIcon, ArrowRightIcon } from 'lucide-react';
import { universities } from '../data/universities';
import { usePageMeta } from '../hooks/usePageMeta';
const brandColors: Record<string, {
  gradient: string;
  accent: string;
  tag: string;
}> = {
  'global-tech-college': {
    gradient: 'bg-gradient-to-br from-[#1B365D] to-[#8B1A1A]',
    accent: 'text-[#D4A843]',
    tag: 'bg-[#1B365D]/10 text-[#1B365D]'
  },
  'ultimate-medical-academy': {
    gradient: 'bg-gradient-to-br from-[#003B5C] to-[#176B9C]',
    accent: 'text-[#00BFA5]',
    tag: 'bg-[#003B5C]/10 text-[#003B5C]'
  },
  'university-of-arizona-global-campus': {
    gradient: 'bg-gradient-to-br from-[#1C2B39] to-[#003366]',
    accent: 'text-[#CC0033]',
    tag: 'bg-[#1C2B39]/10 text-[#1C2B39]'
  },
  'colorado-christian-university': {
    gradient: 'bg-gradient-to-br from-[#0F3057] to-[#1A4A7A]',
    accent: 'text-[#D4A943]',
    tag: 'bg-[#0F3057]/10 text-[#0F3057]'
  }
};
const defaultBrand = {
  gradient: 'bg-navy',
  accent: 'text-gold',
  tag: 'bg-navy/5 text-navy'
};
export function CollegesPage() {
  usePageMeta('Browse Online Colleges & Universities | OnlineCollegeGuide', 'Explore accredited online colleges and universities. Compare programs, read reviews, and request free information from top schools.', {
    ogImage: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  return <main className="flex-grow bg-light" data-id="element-736">
      {/* Hero */}
      <div className="bg-navy text-white py-20" data-id="element-737">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-738">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 text-gold text-sm font-semibold mb-6" data-id="element-739">
            <BuildingIcon className="w-4 h-4 mr-2" data-id="element-740" />
            Partner Schools
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 text-white" data-id="element-741">
            Get More Information About
            <br className="hidden sm:block" data-id="element-742" /> Our Partner Colleges
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed" data-id="element-743">
            Explore accredited colleges and universities offering online
            programs. Select a school to learn more and request information.
          </p>
        </div>
      </div>

      {/* Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-id="element-744">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-745">
          {universities.map(uni => {
          const brand = brandColors[uni.slug] || defaultBrand;
          return <Link key={uni.slug} to={`/colleges/${uni.slug}`} className="group bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-xl hover:border-gray-300 transition-all duration-300 flex flex-col" data-id="element-746">
                {/* Card Header with School Brand Colors */}
                <div className={`${brand.gradient} p-8 flex items-center gap-5 relative overflow-hidden`} data-id="element-747">
                  {/* Decorative circle */}
                  <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/5" data-id="element-748"></div>
                  <div className="absolute -right-4 -bottom-10 w-24 h-24 rounded-full bg-white/5" data-id="element-749"></div>

                  {uni.logoUrl ? <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center flex-shrink-0 overflow-hidden p-2 shadow-lg" data-id="element-750">
                      <img src={uni.logoUrl} alt={`${uni.name} logo`} className="w-full h-full object-contain" data-id="element-751" />
                    </div> : <div className="w-16 h-16 rounded-xl bg-white/15 border-2 border-white/25 flex items-center justify-center flex-shrink-0" data-id="element-752">
                      <GraduationCapIcon className="w-8 h-8 text-white" data-id="element-753" />
                    </div>}
                  <div className="relative z-10" data-id="element-754">
                    <h2 className="text-xl font-bold text-white leading-tight" data-id="element-755">
                      {uni.name}
                    </h2>
                    <div className="flex items-center text-white/70 text-sm mt-1.5" data-id="element-756">
                      <MapPinIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-757" />
                      {uni.location}
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-7 flex flex-col flex-grow" data-id="element-758">
                  <p className="text-sm text-gray-600 mb-5 leading-relaxed line-clamp-3" data-id="element-759">
                    {uni.description}
                  </p>

                  {/* Accreditation */}
                  <div className="flex items-start text-xs text-gray-500 mb-5 bg-gray-50 rounded-lg p-3" data-id="element-760">
                    <AwardIcon className="w-4 h-4 mr-2 text-gold flex-shrink-0 mt-0.5" data-id="element-761" />
                    <span className="leading-relaxed" data-id="element-762">{uni.accreditation}</span>
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-763">
                    <div className="flex items-center text-xs text-gray-500" data-id="element-764">
                      <PhoneIcon className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" data-id="element-765" />
                      <span data-id="element-766">{uni.phone}</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-500" data-id="element-767">
                      <MailIcon className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" data-id="element-768" />
                      <span className="truncate" data-id="element-769">{uni.email}</span>
                    </div>
                  </div>

                  {/* Programs Preview */}
                  <div className="pt-5 border-t border-gray-100" data-id="element-770">
                    <div className="flex items-center text-xs font-bold text-gray-800 uppercase tracking-wider mb-3" data-id="element-771">
                      <BookOpenIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-772" />
                      {uni.programs.length} Programs Available
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-773">
                      {uni.programs.slice(0, 3).map(prog => <span key={prog.slug} className={`text-xs ${brand.tag} px-2.5 py-1.5 rounded-lg font-medium`} data-id="element-774">
                          {prog.shortTitle}
                        </span>)}
                      {uni.programs.length > 3 && <span className="text-xs text-gray-400 px-2 py-1.5" data-id="element-775">
                          +{uni.programs.length - 3} more
                        </span>}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-6 pt-5 border-t border-gray-100 flex items-center justify-between" data-id="element-776">
                    <span className="text-sm font-bold text-navy group-hover:text-gold transition-colors duration-200" data-id="element-777">
                      View Programs & Request Info
                    </span>
                    <div className="w-10 h-10 rounded-full bg-navy/5 flex items-center justify-center group-hover:bg-navy group-hover:text-white transition-all duration-200" data-id="element-778">
                      <ArrowRightIcon className="w-4 h-4 text-navy group-hover:text-white transition-colors duration-200" data-id="element-779" />
                    </div>
                  </div>
                </div>
              </Link>;
        })}
        </div>

        {/* Empty State */}
        {universities.length === 0 && <div className="text-center py-16" data-id="element-780">
            <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-781" />
            <h3 className="text-xl font-bold text-navy mb-2" data-id="element-782">
              No colleges listed yet
            </h3>
            <p className="text-gray-500" data-id="element-783">
              Check back soon — we're adding new partner schools.
            </p>
          </div>}
      </div>
    </main>;
}